import React, { useState, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, CheckCircle, AlertCircle, Loader, Building2 } from 'lucide-react';

import Button from '../../components/ui/Button';
import {
  Container,
  StickyHeader,
  Title,
  StepIndicator,
  StepDot,
  StepLine,
  StepLabel,
  Card,
  DropZone,
  DropText,
  FileName,
  SummaryGrid,
  SummaryCard,
  SummaryValue,
  SummaryLabel,
  Footer,
  HistoryTable,
  StatusBadge,
  TargetBanner,
  SectionTitle,
} from '../../components/import/ImportStyles';
import { matchEmployeePhotos, applyEmployeePhotos } from '../../services/employeePhotoService';
import { presignAndUploadFile } from '../../services/storageService';
import { useAuth } from '../../context/AuthContext';
import {
  ACCEPTED_TYPES, APPLY_CHUNK, MAX_FILES, UPLOAD_CONCURRENCY,
  buildRows, uploadableRows, countByStatus, chunk, mapWithConcurrency,
} from './photoImport';

// Toplu profil fotoğrafı yükleme (2026-10-19, vMind Kolay İK geçişi). Dosya adı sicil no ya da kurumsal e-posta olan
// fotoğraflar seçilir; sunucu ADLARI çalışanlarla eşler, eşleşen her fotoğraf tarayıcıdan doğrudan depoya yüklenir ve
// çalışana bağlanır. Bileşen toast basmaz (kural) — sonuçlar satır içinde.
const MUTED = { fontFamily: 'Nunito Sans', fontSize: 13, color: '#667085' };

export default function EmployeePhotoImportPage() {
  const { t } = useTranslation();
  const { accessibleTenants, activeTenantId, canSwitchTenant } = useAuth();
  const activeTenant = accessibleTenants.find((tn) => tn.id === activeTenantId);
  const [step, setStep] = useState(1);
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [matching, setMatching] = useState(false);
  const [rows, setRows] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState(null);
  const fileInputRef = useRef(null);

  const handleFilesSelect = useCallback((list) => {
    const picked = Array.from(list || []).filter((f) => ACCEPTED_TYPES.includes(f.type));
    setFiles(picked.slice(0, MAX_FILES));
    setRows(null);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    handleFilesSelect(e.dataTransfer.files);
  }, [handleFilesSelect]);

  const handleMatch = useCallback(async () => {
    if (files.length === 0) return;
    setMatching(true);
    try {
      const res = await matchEmployeePhotos(files.map((f) => f.name));
      setRows(buildRows(files, res.data?.data || []));
      setStep(2);
    } catch {
      // Handled by interceptor
    } finally {
      setMatching(false);
    }
  }, [files]);

  const toUpload = useMemo(() => (rows ? uploadableRows(rows) : []), [rows]);
  const counts = useMemo(() => (rows ? countByStatus(rows) : {}), [rows]);

  const handleUpload = useCallback(async () => {
    if (toUpload.length === 0) return;
    setUploading(true);
    setProgress({ done: 0, total: toUpload.length });
    setStep(3);

    // 1) Depoya yükleme (eşzamanlı, hata satırda kalır).
    const uploaded = await mapWithConcurrency(toUpload, UPLOAD_CONCURRENCY, async (row) => {
      try {
        const fileUrl = await presignAndUploadFile(row.file, 'avatar');
        return { row, fileUrl };
      } catch {
        return { row, error: 'employeePhoto.uploadFailed' };
      }
    }, (done) => setProgress({ done, total: toUpload.length }));

    // 2) Çalışana bağlama (sunucu sınırı kadar parçalar).
    const final = uploaded.filter((u) => u.error).map((u) => ({ row: u.row, status: 'error', message: u.error }));
    const ok = uploaded.filter((u) => !u.error);
    for (const part of chunk(ok, APPLY_CHUNK)) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const res = await applyEmployeePhotos(part.map((u) => ({ employeeId: u.row.employeeId, fileUrl: u.fileUrl })));
        const byEmployee = new Map((res.data?.data || []).map((r) => [r.employeeId, r]));
        part.forEach((u) => {
          const r = byEmployee.get(u.row.employeeId);
          final.push({ row: u.row, status: r?.status === 'updated' ? 'updated' : 'error', message: r?.message || null });
        });
      } catch {
        part.forEach((u) => final.push({ row: u.row, status: 'error', message: 'employeePhoto.applyFailed' }));
      }
    }
    setResults(final);
    setUploading(false);
  }, [toUpload]);

  const handleReset = useCallback(() => {
    setStep(1);
    setFiles([]);
    setRows(null);
    setResults(null);
    setProgress({ done: 0, total: 0 });
  }, []);

  const steps = [
    { label: t('employeePhotoImport.step1') },
    { label: t('employeePhotoImport.step2') },
    { label: t('employeePhotoImport.step3') },
  ];

  const updatedCount = results ? results.filter((r) => r.status === 'updated').length : 0;
  const failed = results ? results.filter((r) => r.status !== 'updated') : [];
  const statusLabel = (status) => t(`employeePhotoImport.status.${status}`);
  const errorLabel = (key) => (key ? t(`responseErrors.${key}`, { defaultValue: key }) : t('employeePhotoImport.applyFailedGeneric'));

  return (
    <Container>
      <StickyHeader>
        <Title>{t('employeePhotoImport.title')}</Title>
        {activeTenant && (
          <TargetBanner>
            <Building2 size={18} />
            <span>
              {t(
                canSwitchTenant ? 'employeePhotoImport.targetTenantInfoSwitchable' : 'employeePhotoImport.targetTenantInfo',
                { tenant: activeTenant.name },
              )}
            </span>
          </TargetBanner>
        )}
        <StepIndicator>
          {steps.map((s, i) => (
            <React.Fragment key={s.label}>
              {i > 0 && <StepLine $completed={step > i} />}
              <StepDot $active={step === i + 1} $completed={step > i + 1}>
                {step > i + 1 ? '✓' : i + 1}
              </StepDot>
              <StepLabel $active={step === i + 1}>{s.label}</StepLabel>
            </React.Fragment>
          ))}
        </StepIndicator>
      </StickyHeader>

      {/* Step 1: Select photos */}
      {step === 1 && (
        <Card>
          <SectionTitle>{t('employeePhotoImport.selectTitle')}</SectionTitle>
          <p style={{ ...MUTED, fontSize: 14, marginBottom: 16 }}>{t('employeePhotoImport.selectDesc')}</p>
          <DropZone
            $dragOver={dragOver}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            data-testid="photo-dropzone"
          >
            <Upload size={32} color="#667085" />
            <DropText>
              {files.length > 0
                ? <FileName>{t('employeePhotoImport.selectedCount', { count: files.length })}</FileName>
                : t('employeePhotoImport.dropzone')}
            </DropText>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPTED_TYPES.join(',')}
              style={{ display: 'none' }}
              data-testid="photo-input"
              onChange={(e) => handleFilesSelect(e.target.files)}
            />
          </DropZone>
          <p style={{ ...MUTED, marginTop: 12 }}>{t('employeePhotoImport.limits')}</p>
          <Footer>
            <div />
            <Button onClick={handleMatch} disabled={files.length === 0 || matching}>
              {matching ? <Loader size={16} className="spin" style={{ marginRight: 8 }} /> : null}
              {t('employeePhotoImport.match')}
            </Button>
          </Footer>
        </Card>
      )}

      {/* Step 2: Match preview */}
      {step === 2 && rows && (
        <Card>
          <SummaryGrid>
            <SummaryCard $color="#F0F9FF">
              <SummaryValue>{rows.length}</SummaryValue>
              <SummaryLabel>{t('employeePhotoImport.totalFiles')}</SummaryLabel>
            </SummaryCard>
            <SummaryCard $color="#F0FDF4">
              <SummaryValue>{toUpload.length}</SummaryValue>
              <SummaryLabel>{t('employeePhotoImport.toUpload')}</SummaryLabel>
            </SummaryCard>
            <SummaryCard $color="#FFFBEB">
              <SummaryValue>{toUpload.filter((r) => r.hasPhoto).length}</SummaryValue>
              <SummaryLabel>{t('employeePhotoImport.willReplace')}</SummaryLabel>
            </SummaryCard>
            <SummaryCard $color="#FEF2F2">
              <SummaryValue>{rows.length - (counts.matched || 0)}</SummaryValue>
              <SummaryLabel>{t('employeePhotoImport.skipped')}</SummaryLabel>
            </SummaryCard>
          </SummaryGrid>

          <HistoryTable data-testid="photo-match-table">
            <thead>
              <tr>
                <th>{t('employeePhotoImport.col.file')}</th>
                <th>{t('employeePhotoImport.col.status')}</th>
                <th>{t('employeePhotoImport.col.employee')}</th>
                <th>{t('employeePhotoImport.col.registrationNumber')}</th>
                <th>{t('employeePhotoImport.col.currentPhoto')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key}>
                  <td>{r.fileName}</td>
                  <td>
                    <StatusBadge $status={r.status === 'matched' ? 'completed' : 'failed'}>{statusLabel(r.status)}</StatusBadge>
                  </td>
                  <td>{r.fullName || '-'}</td>
                  <td>{r.registrationNumber || '-'}</td>
                  <td>{r.employeeId ? (r.hasPhoto ? t('employeePhotoImport.replaced') : t('employeePhotoImport.none')) : '-'}</td>
                </tr>
              ))}
            </tbody>
          </HistoryTable>

          <Footer>
            <Button variant="ghost" onClick={handleReset}>{t('employeePhotoImport.back')}</Button>
            <Button onClick={handleUpload} disabled={toUpload.length === 0 || uploading}>
              {t('employeePhotoImport.upload', { count: toUpload.length })}
            </Button>
          </Footer>
        </Card>
      )}

      {/* Step 3: Progress & result */}
      {step === 3 && (
        <Card>
          {uploading || !results ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Loader size={40} color="#0291EF" className="spin" />
              <p style={{ fontFamily: 'Nunito Sans', fontSize: 16, fontWeight: 600, marginTop: 16 }}>
                {t('employeePhotoImport.uploading', { done: progress.done, total: progress.total })}
              </p>
              <p style={MUTED}>{t('employeePhotoImport.uploadingDesc')}</p>
            </div>
          ) : (
            <>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                {failed.length === 0
                  ? <CheckCircle size={48} color="#16a34a" />
                  : <AlertCircle size={48} color="#D97706" />}
                <p style={{ fontFamily: 'Nunito Sans', fontSize: 18, fontWeight: 700, marginTop: 12 }}>
                  {t('employeePhotoImport.done', { count: updatedCount })}
                </p>
              </div>
              {failed.length > 0 && (
                <>
                  <SectionTitle>{t('employeePhotoImport.failedTitle')}</SectionTitle>
                  <HistoryTable data-testid="photo-failed-table">
                    <thead>
                      <tr>
                        <th>{t('employeePhotoImport.col.file')}</th>
                        <th>{t('employeePhotoImport.col.employee')}</th>
                        <th>{t('employeePhotoImport.col.reason')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {failed.map((r) => (
                        <tr key={r.row.key}>
                          <td>{r.row.fileName}</td>
                          <td>{r.row.fullName || '-'}</td>
                          <td>{errorLabel(r.message)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </HistoryTable>
                </>
              )}
              <Footer>
                <div />
                <Button onClick={handleReset}>{t('employeePhotoImport.newUpload')}</Button>
              </Footer>
            </>
          )}
        </Card>
      )}
    </Container>
  );
}
